import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaDownload, FaPrint, FaGlobe, FaPhoneAlt, FaEnvelope, FaBuilding } from 'react-icons/fa';
import './UserModals.css';

const InvoiceModal = ({ isOpen, onClose, invoice }) => {
    useEffect(() => {
        // Print-specific style addition
        const style = document.createElement('style');
        style.innerHTML = `
            @media print {
                /* Hide everything by default */
                body * {
                    visibility: hidden;
                }
                /* Show ONLY the modal overlay and its children */
                .modal-overlay, .modal-overlay * {
                    visibility: visible;
                }
                /* Ensure the modal overlay is positioned at the top-left */
                .modal-overlay {
                    position: absolute !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 100% !important;
                    background: white !important;
                    backdrop-filter: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    display: block !important;
                    z-index: 999999 !important;
                }
                /* Hide UI elements that shouldn't be printed */
                .modal-actions, .modal-close-btn {
                    display: none !important;
                }
                /* Content styling for paper */
                .modal-content {
                    box-shadow: none !important;
                    width: 100% !important;
                    max-width: none !important;
                    border: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
                .modal-header {
                    background: white !important;
                    border-bottom: 2px solid #0f172a !important;
                    padding: 20px 0 !important;
                }
                .modal-body {
                    padding: 0 !important;
                    background: white !important;
                }
            }
        `;
        document.head.appendChild(style);

        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.head.removeChild(style);
        };
    }, [isOpen]);

    if (!invoice) return null;

    return ReactDOM.createPortal(
        <div className={`modal-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px', background: '#fff' }}>
                <div className="modal-header" style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', padding: '24px 40px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ background: 'var(--primary-blue)', color: 'white', padding: '12px', borderRadius: '12px' }}>
                            <FaBuilding size={24} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.5rem', fontWeight: 800 }}>Platform Enterprise</h2>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Official Tax Invoice</span>
                        </div>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="modal-body" style={{ padding: '40px', background: '#fff' }}>
                    {/* Invoice Meta Info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px', borderBottom: '1px solid #f1f5f9', paddingBottom: '30px' }}>
                        <div>
                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Billed From</div>
                            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>Course Management Platform</div>
                            <div style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
                                <p style={{ margin: 0 }}>High Street Corporate Tower</p>
                                <p style={{ margin: 0 }}>Business District, Level 42</p>
                                <p style={{ margin: 0 }}>support@platform.com</p>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Invoice Details</div>
                            <div style={{ fontWeight: 800, color: 'var(--primary-blue)', fontSize: '20px' }}>#{invoice.invoiceNo}</div>
                            <div style={{ color: '#0f172a', fontSize: '14px', fontWeight: 600, marginTop: '5px' }}>Date: {new Date(invoice.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                            <div style={{ marginTop: '10px' }}>
                                <span style={{
                                    background: invoice.status === 'Paid' ? '#dcfce7' : '#fee2e2',
                                    color: invoice.status === 'Paid' ? '#166534' : '#991b1b',
                                    padding: '4px 12px',
                                    borderRadius: '20px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase'
                                }}>{invoice.status}</span>
                            </div>
                        </div>
                    </div>

                    {/* Customer Info */}
                    <div style={{ marginBottom: '40px' }}>
                        <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>Bill To</div>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '18px' }}>{invoice.studentId?.name || 'N/A'}</div>
                        <div style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
                            <p style={{ margin: 0 }}>{invoice.studentId?.email || 'N/A'}</p>
                            <p style={{ margin: 0 }}>Student ID: {invoice.studentId?._id?.substring(0, 8).toUpperCase()}</p>
                        </div>
                    </div>

                    {/* Items Table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #0f172a' }}>
                                <th style={{ textAlign: 'left', padding: '12px 0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>Description</th>
                                <th style={{ textAlign: 'center', padding: '12px 0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>Qty</th>
                                <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>Unit Price</th>
                                <th style={{ textAlign: 'right', padding: '12px 0', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '20px 0' }}>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{invoice.courseId?.title || 'N/A'}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Complete Access to course materials and certifications.</div>
                                </td>
                                <td style={{ textAlign: 'center', padding: '20px 0', color: '#0f172a' }}>01</td>
                                <td style={{ textAlign: 'right', padding: '20px 0', color: '#0f172a' }}>${invoice.amount.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '20px 0', fontWeight: 700, color: '#0f172a' }}>${invoice.amount.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Summary Section */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: '300px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ color: '#64748b' }}>Subtotal</span>
                                <span style={{ color: '#0f172a', fontWeight: 600 }}>${invoice.amount.toLocaleString()}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ color: '#64748b' }}>Tax (GST 0%)</span>
                                <span style={{ color: '#0f172a', fontWeight: 600 }}>$0.00</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0', marginTop: '10px' }}>
                                <span style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>Total Amount</span>
                                <span style={{ fontSize: '20px', fontWeight: 800, color: 'var(--primary-blue)' }}>${invoice.amount.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Payment Info */}
                    <div style={{ marginTop: '40px', padding: '24px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Payment Transaction Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>Payment ID</div>
                                <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 600, fontFamily: 'monospace' }}>{invoice.paymentId || 'N/A'}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>Method</div>
                                <div style={{ fontSize: '14px', color: '#0f172a', fontWeight: 600 }}>Razorpay Secure Checkout</div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions" style={{ borderTop: 'none', marginTop: '40px' }}>
                        <button type="button" className="action-btn-secondary" onClick={onClose} style={{ borderRadius: '50px' }}>
                            Dismiss
                        </button>
                        <button
                            type="button"
                            className="action-btn-gold"
                            onClick={() => window.print()}
                            style={{ borderRadius: '50px', padding: '14px 40px' }}
                        >
                            <FaPrint style={{ marginRight: '10px' }} /> Print Invoice
                        </button>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: '40px', color: '#94a3b8', fontSize: '12px' }}>
                        <p>This is a computer-generated document. No signature is required.</p>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '10px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaGlobe /> www.platform.com</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaPhoneAlt /> +1 (555) 000-1234</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaEnvelope /> finance@platform.com</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default InvoiceModal;
